import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { useAnchor } from "hooks";
import { TickIcon } from "icons/TickIcon";
import { Features3Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Features3Content;
}

const Features3: VFC<Props> = withErrorBoundary(
  ({ content: { features3Title, features3Description, features3List } }) => {
    useAnchor("features3");

    return (
      <StyledFeatures3 className="features3 Content">
        <div className="Features3__container">
          <h2 className="Font_48_54_600 xl:Font_40_48_600">{features3Title}</h2>
          <Markdown className="Features3__description Color_secondary Font_24_32_450 xl:Font_18_26_450">
            {features3Description}
          </Markdown>
          <div className="Features3__listContainer">
            <ul className="Features3__list">
              {features3List.map(({ features3ListText }) => (
                <li key={features3ListText} className="Features3__listItem">
                  <TickIcon className="Features3__listItemIcon" />
                  <h3 className="Features3__listItemText Font_18_26_450 xl:Font_16_22_450 sm:Font_14_20_450">
                    {features3ListText}
                  </h3>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </StyledFeatures3>
    );
  },
  {}
);

const StyledFeatures3 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Features3__container {
    padding: 80px;
    background: url("/images/noise.png"),
      radial-gradient(71.26% 112.79% at 114.07% -12.79%, rgb(85 50 250 / 80%) 0%, rgb(205 216 220 / 0%) 100%),
      radial-gradient(76.41% 47.92% at 90.57% 100%, #fccf8f 0.89%, rgb(205 216 220 / 0%) 100%), #faf6f4;
    background-blend-mode: overlay, normal, normal;
    background-repeat: no-repeat;
  }

  .Features3__description {
    margin-top: 24px;
  }

  .Features3__listContainer {
    max-width: 889px;
  }

  .Features3__list {
    display: flex;
    flex-wrap: wrap;
    margin-left: -16px;
    margin-top: 16px;
  }

  .Features3__listItem {
    width: fit-content;
    display: flex;
    align-items: center;
    border-radius: 32px;
    padding: 11px 16px;
    margin-top: 24px;
    margin-left: 16px;
    background-color: ${({ theme }) => theme.colors.fog["00"]};
  }

  .Features3__listItemIcon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .Features3__listItemText {
    margin-left: 12px;
  }

  @media (min-width: 1200px) {
    .Features3__container {
      background-size: 100% 100%;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features3__container {
      padding: 40px 24px;
    }

    .Features3__listContainer {
      max-width: 100%;
    }

    .Features3__list {
      margin-top: 22px;
    }

    .Features3__listItem {
      margin-top: 18px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    &.Content {
      padding-left: 0;
      padding-right: 0;
    }

    .Features3__description {
      margin-top: 16px;
    }

    .Features3__container {
      padding: 40px 24px;
    }

    .Features3__list {
      flex-flow: column nowrap;
      margin-top: 32px;
    }

    .Features3__listItem {
      align-items: flex-start;
      margin-top: 8px;
      padding: 0;
      background-color: inherit;
    }

    .Features3__listItemIcon {
      margin-top: 2px;
    }
  }
`;

gql`
  fragment Features3Content on ComponentFeaturesFeatures3 {
    features3Title
    features3Description
    features3List {
      features3ListText
    }
  }
`;

export { Features3 };
export type { Props as Features3Props };
